const getPrincipalMemberId = (principal) => principal.memberId || principal.id;

export const getPrincipalMemberIds = (principals = []) => [
  ...new Set(principals.map(getPrincipalMemberId).filter(Boolean)),
];

export const formatFeishuContactDepartmentPaths = (departmentPaths = []) =>
  departmentPaths.join('；');

export const formatFeishuContactLabel = (contact) => {
  const name = contact.name || contact.externalUserId;
  const departmentPaths = formatFeishuContactDepartmentPaths(contact.departmentPaths);
  return departmentPaths ? `${name} · ${departmentPaths}` : name;
};

export const formatOperationEntityPrincipalNames = (principals = []) => {
  const names = mergeOperationEntityPrincipals(principals)
    .map((principal) => principal.name || principal.phoneNumber || getPrincipalMemberId(principal))
    .filter(Boolean);
  return names.length ? names.join('、') : '-';
};

export const formatRecommendedContact = (contact, departmentPath) => {
  if (!contact) {
    return '未匹配';
  }
  const path = departmentPath || contact.departmentPaths?.[0];
  return [path, contact.name || contact.externalUserId].filter(Boolean).join(' / ');
};

export const mergeOperationEntityPrincipals = (principals = []) => {
  const principalByMemberId = new Map();
  principals.forEach((principal) => {
    const memberId = getPrincipalMemberId(principal);
    if (!memberId) {
      return;
    }
    const existing = principalByMemberId.get(memberId);
    if (!existing) {
      principalByMemberId.set(memberId, principal);
      return;
    }
    principalByMemberId.set(memberId, {
      ...existing,
      ...principal,
    });
  });
  return [...principalByMemberId.values()];
};

export const buildRecommendationConfirmation = ({ entities, items: matchItems }) => {
  const matchByEntityId = Object.fromEntries(
    matchItems.map((item) => [item.operationEntityId, item]),
  );
  const items = [];

  entities.forEach((entity) => {
    if (!entity.parentId || !entity.enabled) {
      return;
    }
    const match = matchByEntityId[entity.id] || {};
    const externalUserId = match.recommendedContact?.externalUserId;
    if (!externalUserId || !['pending', 'unconfirmed'].includes(match.status)) {
      return;
    }
    items.push({ externalUserIds: [externalUserId], operationEntityId: entity.id });
  });

  return { items };
};

export const getRecommendationIssue = (item) => {
  if (item?.status === 'conflict') return 'conflict';
  if (item?.status === 'error') return 'unmatched';
  return null;
};

export const filterOperationEntitiesByRecommendation = (
  entities = [],
  matchItems = [],
  filter = 'all',
) => {
  if (filter === 'all') return entities;
  const entityById = new Map(entities.map((entity) => [entity.id, entity]));
  const matchingEntityIds = new Set(
    matchItems
      .filter((item) => getRecommendationIssue(item) === filter)
      .map((item) => item.operationEntityId),
  );
  const visibleEntityIds = new Set();
  entities.forEach((entity) => {
    if (!matchingEntityIds.has(entity.id)) return;
    let current = entity;
    while (current) {
      visibleEntityIds.add(current.id);
      current = entityById.get(current.parentId);
    }
  });
  return entities.filter((entity) => visibleEntityIds.has(entity.id));
};

const MEMBER_PREFIX = 'member:';
const FEISHU_PREFIX = 'feishu:';
const FEISHU_DUPLICATE_PREFIX = 'feishu-duplicate:';

export const buildPrincipalCandidateTree = ({
  contactTree = [],
  groupContactTree = [],
  memberOptions = [],
  recommendedExternalUserId,
}) => {
  const contactOccurrenceById = new Map();
  const mergeSameNameRoots = (departments) => {
    const rootByName = new Map();
    departments.forEach((department) => {
      let root = rootByName.get(department.name);
      if (!root) {
        root = {
          children: [],
          contactsByUserId: new Map(),
          department,
        };
        rootByName.set(department.name, root);
      }
      root.children.push(...(department.children || []));
      [department.leader, ...(department.members || [])].forEach((contact) => {
        const userId = String(contact?.userId || '').trim();
        if (userId && !root.contactsByUserId.has(userId)) {
          root.contactsByUserId.set(userId, contact);
        }
      });
    });
    return [...rootByName.values()].map(({ children, contactsByUserId, department }) => ({
      ...department,
      children,
      leader: null,
      members: [...contactsByUserId.values()],
    }));
  };
  const buildDepartments = (departments, valuePrefix = '') =>
    departments
      .map((department) => {
        const contacts = [department.leader, ...(department.members || [])]
          .map((contact) => {
            const externalUserId = String(contact?.userId || '').trim();
            if (!externalUserId) return null;
            const occurrence = (contactOccurrenceById.get(externalUserId) || 0) + 1;
            contactOccurrenceById.set(externalUserId, occurrence);
            const name = String(contact?.name || externalUserId).trim();
            return {
              title: externalUserId === recommendedExternalUserId ? `${name}（推荐）` : name,
              value:
                occurrence === 1
                  ? `${FEISHU_PREFIX}${externalUserId}`
                  : `${FEISHU_DUPLICATE_PREFIX}${occurrence}:${externalUserId}`,
            };
          })
          .filter(Boolean);
        const children = [...contacts, ...buildDepartments(department.children || [], valuePrefix)];
        if (!children.length) return null;
        return {
          children,
          selectable: false,
          title: department.name,
          value: `${valuePrefix}department:${department.id}`,
        };
      })
      .filter(Boolean);
  const tree = [];
  if (memberOptions.length) {
    tree.push({
      children: memberOptions.map((option) => ({
        title: option.label,
        value: `${MEMBER_PREFIX}${option.value}`,
      })),
      selectable: false,
      title: '当前负责人',
      value: 'source:dpa',
    });
  }
  const departments = buildDepartments(mergeSameNameRoots(contactTree));
  tree.push(...departments);
  const groupDepartments = buildDepartments(mergeSameNameRoots(groupContactTree), 'group:');
  if (groupDepartments.length) {
    tree.push({
      children: groupDepartments,
      selectable: false,
      title: '企业通讯录',
      value: 'source:feishu-group',
    });
  }
  return tree;
};

export const getPrincipalCandidateIds = (principals = []) =>
  getPrincipalMemberIds(principals).map((memberId) => `${MEMBER_PREFIX}${memberId}`);

const normalizeDepartmentMatchName = (value) =>
  String(value || '')
    .trim()
    .replace(/\s*[（(].*$/u, '')
    .trim();

const findContactDepartments = (contactTree, departmentName) => {
  const matches = [];
  const normalizedDepartmentName = normalizeDepartmentMatchName(departmentName);
  if (!normalizedDepartmentName) return matches;
  const visit = (departments) => {
    for (const department of departments) {
      if (normalizeDepartmentMatchName(department.name) === normalizedDepartmentName) {
        matches.push(department);
      }
      visit(department.children || []);
    }
  };
  visit(contactTree);
  return matches;
};

export const getPrincipalContactTreesForEntity = (
  contactTree = [],
  { entityName, isRoot = false, parentName } = {},
) => {
  const matchName = normalizeDepartmentMatchName(entityName) ? entityName : parentName;
  return {
    contactTree: isRoot ? [] : findContactDepartments(contactTree, matchName),
    groupContactTree: contactTree,
  };
};

const getFeishuCandidateUserId = (value) => {
  if (value.startsWith(FEISHU_PREFIX)) {
    return value.slice(FEISHU_PREFIX.length);
  }
  if (value.startsWith(FEISHU_DUPLICATE_PREFIX)) {
    return value.split(':').slice(2).join(':');
  }
  return null;
};

export const reconcilePrincipalCandidateIds = (candidateIds = [], tree = []) => {
  const candidateValueByExternalUserId = new Map();
  const visit = (nodes) => {
    nodes.forEach((node) => {
      const externalUserId = getFeishuCandidateUserId(node.value || '');
      if (externalUserId && !candidateValueByExternalUserId.has(externalUserId)) {
        candidateValueByExternalUserId.set(externalUserId, node.value);
      }
      visit(node.children || []);
    });
  };
  visit(tree);
  return [
    ...new Set(
      candidateIds
        .map((value) => {
          if (value.startsWith(MEMBER_PREFIX)) return value;
          const externalUserId = getFeishuCandidateUserId(value);
          return candidateValueByExternalUserId.get(externalUserId) || null;
        })
        .filter(Boolean),
    ),
  ];
};

export const splitPrincipalCandidateIds = (candidateIds = []) => ({
  externalUserIds: [...new Set(candidateIds.map(getFeishuCandidateUserId).filter(Boolean))],
  memberIds: [
    ...new Set(
      candidateIds
        .filter((value) => value.startsWith(MEMBER_PREFIX))
        .map((value) => value.slice(MEMBER_PREFIX.length)),
    ),
  ],
});
